import React, { useEffect } from 'react';
import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { getAssociateImageQuality, logImageQualityAction } from './api';
import { IMAGE_QUALITY_ASSOCIATE_VIEW } from '../../utils/metric-utils';
import { ImageQualityCheckId, ReportWindow } from './model';
import {
  formatCycleTime,
  formatOfdDate,
  formatPct,
  largestDriver,
  passRatePct,
  qualityTier,
  sharePct,
} from './imageQualityUtils';
import { useReportFetch, reportRequestKey } from './useReportFetch';
import FailureBreakdown from './FailureBreakdown';
import PhotoGrid from './PhotoGrid';
import {
  AccentCard,
  Label,
  LoadFailed,
  PageTitle,
  PrintButton,
  textColorForTier,
} from './meridianUi';

/** Drill-down for one associate: stats strip, where-to-focus summary + per-check bars, photos worst first. */
export interface AssociateDetailProps {
  /** Told the earliest operating day with data, so the page calendars can start there. */
  onDataAvailableFrom?: (day?: string) => void;
  warehouseId: string;
  reportWindow: ReportWindow;
  windowLabel: string;
  login: string;
}

const CHECK_ORDER: ImageQualityCheckId[] = ['PII', 'BLUR', 'DAMAGE'];

// Four photos per row on Meridian's 12-column grid.
const PHOTO_GRID_WIDTH = 'grid-3';

const AssociateDetail: React.FC<AssociateDetailProps> = ({
  onDataAvailableFrom,
  warehouseId,
  reportWindow,
  windowLabel,
  login,
}) => {
  const {
    data: detail,
    loading,
    failed,
    retry,
  } = useReportFetch(
    () => getAssociateImageQuality({ warehouseId, login, ...reportWindow }),
    reportRequestKey(reportWindow, warehouseId, login)
  );

  useEffect(() => {
    onDataAvailableFrom?.(detail?.dataAvailableFrom);
  }, [detail, onDataAvailableFrom]);

  useEffect(() => {
    logImageQualityAction(IMAGE_QUALITY_ASSOCIATE_VIEW);
  }, [login]);

  const header = (
    <>
      <Row alignmentHorizontal="justify" alignmentVertical="top">
        <PageTitle
          title={login}
          sub={[
            `Site ${warehouseId}`,
            windowLabel,
            ...(detail?.associate.avgCycleTimeSeconds !== undefined
              ? [`Avg cycle time ${formatCycleTime(detail.associate.avgCycleTimeSeconds)}`]
              : []),
          ].join(' · ')}
          mono
        />
        <PrintButton />
      </Row>
    </>
  );

  if (loading || !detail) {
    return (
      <Column spacing="500">
        {header}
        {loading && (
          <Row alignmentHorizontal="center" spacingInset="500">
            <Loader size="large" />
          </Row>
        )}
        {failed && (
          <LoadFailed
            message="Couldn't load this associate's photos."
            onRetry={retry}
            testId="iq-associate-failed"
          />
        )}
      </Column>
    );
  }

  const { associate, thresholds, failureBreakdown, photos, heaviestFailureDay } = detail;
  const failing = associate.totalPhotos - associate.passingPhotos;
  const pct = passRatePct(associate.passingPhotos, associate.totalPhotos);
  const tier = qualityTier(pct, thresholds);
  const driver = largestDriver(failureBreakdown);
  const others = failureBreakdown
    .filter(check => check.failureLabel !== driver?.failureLabel && check.failedPhotos > 0)
    .sort((left, right) => right.failedPhotos - left.failedPhotos);

  // Stats strip order follows the mock: totals, quality, then each check (PII, Blur, Damage).
  const stats: {
    key: string;
    label: string;
    value: string;
    color?: 'success' | 'warning' | 'error';
  }[] = [
    { key: 'photos', label: 'Photos', value: `${associate.totalPhotos}` },
    { key: 'passing', label: 'Passing', value: `${associate.passingPhotos}` },
    { key: 'failing', label: 'Failing', value: `${failing}` },
    { key: 'quality', label: 'Quality', value: formatPct(pct), color: textColorForTier(tier) },
    ...[...failureBreakdown]
      .sort((left, right) => CHECK_ORDER.indexOf(left.checkId) - CHECK_ORDER.indexOf(right.checkId))
      .map(check => ({
        key: check.checkId,
        label: check.failureLabel,
        value: `${check.failedPhotos}`,
      })),
    ...(associate.avgCycleTimeSeconds !== undefined
      ? [
          {
            key: 'cycle',
            label: 'Avg cycle time',
            value: formatCycleTime(associate.avgCycleTimeSeconds),
          },
        ]
      : []),
  ];

  return (
    <Column spacing="500" data-testid="iq-associate-detail">
      {header}

      <Row spacing="300" wrap="down">
        {stats.map(stat => (
          <Card key={stat.key} spacingInset="300 400" data-testid={`iq-stat-${stat.key}`}>
            <Column spacing="100" alignmentHorizontal="center">
              <Text type="h500" color={stat.color}>
                {stat.value}
              </Text>
              <Text type="h100" color="secondary">
                {stat.label.toUpperCase()}
              </Text>
            </Column>
          </Card>
        ))}
      </Row>

      <AccentCard
        testId="iq-associate-summary"
        aside={
          <Column spacing="400">
            <Label>Breakdown</Label>
            <FailureBreakdown breakdown={failureBreakdown} scaleTo={associate.totalPhotos} />
          </Column>
        }
      >
        <Text type="b500">
          <strong>{failing}</strong> of <strong>{associate.totalPhotos}</strong> photos did not
          pass, giving <strong>{formatPct(pct)}</strong> image quality.
        </Text>
        {driver && (
          <Text type="b500">
            The largest driver is <strong>{driver.failureLabel}</strong> —{' '}
            <strong>{driver.failedPhotos}</strong> photos (
            {formatPct(sharePct(driver.failedPhotos, associate.totalPhotos))} of everything
            captured).
            {others.length > 0 &&
              ` ${others.map(other => `${other.failureLabel} accounts for ${other.failedPhotos}`).join('; ')}.`}
          </Text>
        )}
        {heaviestFailureDay && (
          <Text type="b500" data-testid="iq-heaviest-day">
            Failures are heaviest on <strong>{formatOfdDate(heaviestFailureDay.date)}</strong> (
            {heaviestFailureDay.failedPhotos} of {failing}).
          </Text>
        )}
      </AccentCard>

      <Row spacing="300" alignmentVertical="baseline">
        <Heading level={3} type="h400">
          Photos — worst first
        </Heading>
        <Text type="b400" color="secondary">{`${photos.length} of ${associate.totalPhotos}`}</Text>
      </Row>
      <PhotoGrid warehouseId={warehouseId} photos={photos} cardWidth={PHOTO_GRID_WIDTH} />
    </Column>
  );
};

export default AssociateDetail;
